import { useToast } from '@dapp/multi-lib/ui/toast';
import { ButtonV2 } from '@dapp/multi-lib/ui/button';
import {
  NetworkChainIds,
  enabledNetworks,
  getNetworkFromChainId,
} from '@dapp/multi-lib/network';
import { erc20Abi } from 'viem';
import { useAtom } from 'jotai';
import { truncate } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  bridgeTokens,
  OftTypes,
} from 'structure/bridge/constants/bridgeTokens';
import { outboundAmountMultiplier } from 'structure/bridge/constants/constants';
import {
  DEFAULT_TOKEN_DECIMALS,
  layerZeroChainIdsByChainId,
} from 'structure/bridge/constants/lazyerZeroChains';
import { MessageStatus } from 'structure/bridge/constants/messageStatus';
import { bridgeAtom } from 'structure/bridge/store/bridgeAtom';
import {
  TTransaction,
  transactionsAtom,
} from 'structure/bridge/store/transactionsAtom';
import {
  encodeAbiParameters,
  maxUint256,
  parseAbiParameters,
  parseUnits,
  zeroAddress,
} from 'viem';
import {
  useBalance,
  useConnection,
  useReadContract,
  useWriteContract,
  useSimulateContract,
  useSwitchChain,
  useBlockNumber,
} from 'wagmi';
import PROXY_OFTV2_ABI from '../../constants/abis/ProxyOFTV2WithFee';
import INDIRECT_OFTV2_ABI from '../../constants/abis/indirectOFTv2WithFee';
import { BridgeInputField } from '../BridgeInputField/BridgeInputField';
import { BridgeTransactions } from '../BridgeTransactions/BridgeTransactions';
import { BridgeUserBalanceAndFee } from '../BridgeUserBalanceAndFee/BridgeUserBalanceAndFee';
import { ConnectButton } from 'components/ConnectButton';
import { useQueryClient } from '@tanstack/react-query';
import { ETH_AVERAGE_BLOCK_TIME } from 'utils/constants/ethConst';
import {
  BridgeNativeFeeQuote,
  getEffectiveBridgeNativeFee,
  getBridgeSendCallParams,
  hasSufficientBridgeAllowance,
} from '../../utils/bridgeStateHelpers';

const defaultEstimatedFee: [bigint, bigint] = [BigInt(0), BigInt(0)];

export const BridgePanel: React.FC = () => {
  const { t } = useTranslation('common');
  const toastV2 = useToast();

  const { chain } = useConnection();
  const { address: userAddress } = useConnection();
  const { data: userNativeBalance } = useBalance({
    address: userAddress,
  });

  const [bridgeState, setBridgeState] = useAtom(bridgeAtom);
  const [transactions, setTransactions] = useAtom(transactionsAtom);

  const [toAddressBytes, setToAddressBytes] = useState<
    `0x${string}` | undefined
  >();
  const [approvalToggle, setApprovalToggle] = useState<boolean>(false);
  const { switchChain } = useSwitchChain();

  const [rendered, setRendered] = useState(false);
  // set rendered on first mount
  useEffect(() => {
    setRendered(true);
  }, []);

  const allowedNetworks = useMemo(() => {
    const chainIds = bridgeTokens
      .filter((token) => token.enabled)
      .map((token) => Object.keys(token.address))
      .flat()
      .filter(
        (chain_id) =>
          enabledNetworks.findIndex((obj) => obj.id === Number(chain_id)) !== -1
      )
      .sort();
    const uniqueChains = [...new Set(chainIds)];
    return uniqueChains.map((chainId) => Number(chainId));
  }, []);

  const chainId = useMemo((): number => {
    if (chain?.id && allowedNetworks.includes(chain.id)) {
      return chain.id;
    }
    return allowedNetworks[0];
  }, [chain, allowedNetworks]);

  // set toAddressBytes for sendFrom usage
  useEffect(() => {
    if (userAddress) {
      const encodedData = encodeAbiParameters(parseAbiParameters('address'), [
        userAddress,
      ]);
      setToAddressBytes(encodedData);
    }
  }, [userAddress]);

  // checks if we enable contract calls. must have oft contract on current network and be connected
  const hasOftAndUserAndNetwork =
    !!chain?.id && // do we have a network
    !!userAddress && // do we have a user address
    !!bridgeState.token?.oftAddress[chainId]; // do we have oft contract on current network

  const sendCallParams = useMemo(
    () => getBridgeSendCallParams(userAddress ?? zeroAddress),
    [userAddress]
  );
  const nativeFeeQuoteKey = useMemo(
    () =>
      [
        chainId,
        bridgeState.dstChainId,
        bridgeState.token?.oftAddress[chainId],
        bridgeState.amount,
        toAddressBytes,
        sendCallParams.adapterParams,
      ].join(':'),
    [
      bridgeState.amount,
      bridgeState.dstChainId,
      bridgeState.token?.oftAddress,
      chainId,
      sendCallParams.adapterParams,
      toAddressBytes,
    ]
  );
  const [cachedNativeFeeQuote, setCachedNativeFeeQuote] =
    useState<BridgeNativeFeeQuote>();

  // is dst chain a proxy?
  const destinationOftIsProxy =
    bridgeState.token?.oftType[bridgeState.dstChainId] === OftTypes.Proxy;

  // get allowance of token in oft for sending
  // we monitor change in allowanceDataOft to set the approval toggle
  const { data: allowanceOft = BigInt(0), queryKey: queryKeyAllowance } =
    useReadContract({
      query: {
        enabled:
          hasOftAndUserAndNetwork && !!bridgeState.token?.address[chainId],
      },
      abi: erc20Abi,
      functionName: 'allowance',
      chainId: chainId,
      address: bridgeState.token?.address[chainId],
      args: [
        userAddress!,
        bridgeState.token?.oftAddress[chainId] ?? zeroAddress,
      ], // user wallet address
    });

  // force render on approval
  useEffect(() => {
    setApprovalToggle(!approvalToggle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowanceOft]);

  // Get user token balance
  const {
    data: userBalance = BigInt(0),
    queryKey: queryKeyBalanceOf,
    isError: isBalanceOfError,
    isLoading: isBalanceOfLoading,
  } = useReadContract({
    query: {
      enabled: hasOftAndUserAndNetwork && !!bridgeState.token?.address[chainId],
    },
    abi: erc20Abi,
    functionName: 'balanceOf',
    chainId: chainId,
    address: bridgeState.token?.address[chainId],
    args: userAddress ? [userAddress] : undefined,
  });

  const sourceTokenBalance =
    isBalanceOfError || isBalanceOfLoading ? BigInt(0) : userBalance;

  const {
    data: userDestinationBalance = BigInt(0),
    queryKey: queryKeyDestinationBalanceOf,
    isError: isDestinationBalanceError,
    isLoading: isDestinationBalanceLoading,
  } = useReadContract({
    query: {
      enabled:
        !!userAddress && !!bridgeState.token?.address[bridgeState.dstChainId],
    },
    abi: erc20Abi,
    functionName: 'balanceOf',
    chainId: bridgeState.dstChainId,
    address: bridgeState.token?.address[bridgeState.dstChainId],
    args: userAddress ? [userAddress] : undefined,
  });

  const destinationTokenBalance =
    isDestinationBalanceError || isDestinationBalanceLoading
      ? BigInt(0)
      : userDestinationBalance;

  useEffect(() => {
    setBridgeState((prevState) =>
      prevState.userBalance === sourceTokenBalance
        ? prevState
        : { ...prevState, userBalance: sourceTokenBalance }
    );
  }, [
    bridgeState.token?.address,
    chainId,
    isBalanceOfError,
    isBalanceOfLoading,
    setBridgeState,
    sourceTokenBalance,
    userAddress,
  ]);

  // Get oft contract outboundAmount if we have a proxy oft contract
  const {
    data: outboundAmountOnProxy = BigInt(0),
    queryKey: queryKeyOutboundAmount,
    isLoading: isOutboundAmountLoading,
    isError: isOutboundAmountError,
  } = useReadContract({
    query: {
      enabled:
        hasOftAndUserAndNetwork &&
        destinationOftIsProxy &&
        !!bridgeState.token?.oftAddress[bridgeState.dstChainId],
    },
    abi:
      bridgeState.token?.oftType?.[bridgeState.dstChainId] === OftTypes.Proxy
        ? PROXY_OFTV2_ABI
        : INDIRECT_OFTV2_ABI,
    functionName: 'outboundAmount',
    address: bridgeState.token?.oftAddress[bridgeState.dstChainId],
    chainId: bridgeState.dstChainId,
  });

  // get token fee quote
  const {
    data: tokenFee = BigInt(0),
    isLoading: isLoadingQuote,
    queryKey: queryKeyQuoteOFTFee,
  } = useReadContract({
    // only enable when we have address and amount
    query: {
      enabled:
        hasOftAndUserAndNetwork &&
        bridgeState.amount > 0 &&
        !!bridgeState.token?.decimals,
    },
    address: bridgeState.token?.oftAddress[chainId] ?? zeroAddress,
    chainId: chainId, // current chainId
    abi:
      bridgeState.token?.oftType?.[bridgeState.dstChainId] === OftTypes.Proxy
        ? PROXY_OFTV2_ABI
        : INDIRECT_OFTV2_ABI,
    functionName: 'quoteOFTFee',
    args: [
      layerZeroChainIdsByChainId[bridgeState?.dstChainId], // dest chainId ftm testnet
      parseUnits(bridgeState.amount.toString(), bridgeState.token?.decimals), // token amount
    ],
  });

  useEffect(() => {
    if (bridgeState.amount > 0) {
      setBridgeState((prevState) => ({
        ...prevState,
        tokenQuoteLoading: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingQuote, bridgeState.amount]);

  useEffect(() => {
    setBridgeState((prevState) => ({
      ...prevState,
      tokenFee: tokenFee,
      tokenQuoteLoading: isLoadingQuote,
    }));
  }, [tokenFee, isLoadingQuote]);

  const queryClient = useQueryClient();
  const { data: blockNumber } = useBlockNumber({
    watch: true,
    cacheTime: ETH_AVERAGE_BLOCK_TIME * 5,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: queryKeyQuoteOFTFee });
    queryClient.invalidateQueries({ queryKey: queryKeyAllowance });
    queryClient.invalidateQueries({ queryKey: queryKeyBalanceOf });
    queryClient.invalidateQueries({ queryKey: queryKeyDestinationBalanceOf });
    queryClient.invalidateQueries({ queryKey: queryKeyOutboundAmount });
  }, [blockNumber, queryClient]);

  // Get native fee estimate
  const {
    data: estimatedSendFee = defaultEstimatedFee,
    isLoading: isEstimatedSendFeeLoading,
    isError: isEstimatedSendFeeError,
  } = useReadContract({
    query: {
      enabled:
        hasOftAndUserAndNetwork &&
        bridgeState.amount > 0 &&
        !!bridgeState.token?.decimals &&
        !!bridgeState.dstChainId,
    },
    address: bridgeState.token?.oftAddress[chainId],
    chainId: chainId, // current chainId
    abi:
      bridgeState.token?.oftType?.[bridgeState.dstChainId] === OftTypes.Proxy
        ? PROXY_OFTV2_ABI
        : INDIRECT_OFTV2_ABI,
    functionName: 'estimateSendFee',
    args: [
      layerZeroChainIdsByChainId[bridgeState.dstChainId],
      toAddressBytes ?? '0x', // to address encoded
      parseUnits(
        bridgeState.amount.toString(),
        bridgeState.token?.decimals ?? 18
      ), // token amount to send
      false, // use zero token
      sendCallParams.adapterParams, // adapter params
    ],
  });

  const currentEstimatedNativeFee =
    !isEstimatedSendFeeLoading && !isEstimatedSendFeeError
      ? estimatedSendFee[0]
      : BigInt(0);
  const nativeFee = getEffectiveBridgeNativeFee(
    currentEstimatedNativeFee,
    nativeFeeQuoteKey,
    cachedNativeFeeQuote
  );

  useEffect(() => {
    if (currentEstimatedNativeFee <= BigInt(0)) {
      return;
    }

    setCachedNativeFeeQuote((previousQuote) =>
      previousQuote?.key === nativeFeeQuoteKey &&
      previousQuote.value === currentEstimatedNativeFee
        ? previousQuote
        : { key: nativeFeeQuoteKey, value: currentEstimatedNativeFee }
    );
  }, [currentEstimatedNativeFee, nativeFeeQuoteKey]);

  useEffect(() => {
    setBridgeState((prevState) =>
      prevState.nativeFee === nativeFee
        ? prevState
        : { ...prevState, nativeFee }
    );
  }, [nativeFee, setBridgeState]);

  const bnTokenAmount = parseUnits(
    bridgeState.amount.toString(),
    bridgeState.token?.decimals ?? DEFAULT_TOKEN_DECIMALS
  );
  const minTokenAmount =
    bnTokenAmount > tokenFee ? bnTokenAmount - tokenFee : BigInt(0);
  const hasSufficientAllowance = hasSufficientBridgeAllowance(
    allowanceOft,
    bnTokenAmount
  );
  const allowanceRequired =
    bnTokenAmount > BigInt(0) && !hasSufficientAllowance;

  // Approve token in oft
  const approvalSimulate = useSimulateContract({
    query: {
      enabled: hasOftAndUserAndNetwork && allowanceRequired,
    },
    abi: erc20Abi,
    functionName: 'approve',
    chainId: chainId,
    address: bridgeState.token?.address[chainId],
    args: [bridgeState.token?.oftAddress[chainId] ?? zeroAddress, maxUint256],
  });

  const { writeContractAsync: approve, status: approveStatus } =
    useWriteContract();
  const approvalRequest = approvalSimulate.data?.request;

  // prepare sendFrom
  const sendFromSimulate = useSimulateContract({
    query: {
      enabled:
        hasOftAndUserAndNetwork &&
        hasSufficientAllowance &&
        bnTokenAmount > BigInt(0) &&
        minTokenAmount > BigInt(0) &&
        !!toAddressBytes &&
        !isLoadingQuote &&
        !isEstimatedSendFeeLoading &&
        !isEstimatedSendFeeError &&
        nativeFee > BigInt(0) &&
        !!bridgeState.token?.oftAddress[chainId] && // has oft contract
        !!bridgeState.token?.decimals,
    }, // has token decimals
    chainId: chainId,
    address: bridgeState.token?.oftAddress[chainId],
    abi:
      bridgeState.token?.oftType?.[bridgeState.dstChainId] === OftTypes.Proxy
        ? PROXY_OFTV2_ABI
        : INDIRECT_OFTV2_ABI,
    functionName: 'sendFrom',
    args: [
      userAddress!, // 'from' address to send tokens
      layerZeroChainIdsByChainId[bridgeState.dstChainId], // dest chainId
      toAddressBytes ?? '0x', // to address encoded 'to' address to send tokens
      parseUnits(
        bridgeState.amount.toString(),
        bridgeState.token?.decimals ?? 18
      ), // amount
      minTokenAmount, // min amount = amount - fee in base units
      sendCallParams,
    ],
    value: nativeFee, // native fee,
  });

  const { writeContractAsync: sendFrom, status: sendStatus } =
    useWriteContract();
  const sendFromRequest = sendFromSimulate.data?.request;

  const handleSwitchNetwork = () => {
    switchChain({ chainId: allowedNetworks[0] });
  };
  const handleApproveOft = async () => {
    try {
      if (!approvalRequest) {
        throw new Error(
          'Approval transaction is still being prepared. Please wait and try again.'
        );
      }

      const txHash = await approve(approvalRequest);

      toastV2({
        title: t('APPROVE'),
        hash: truncate(txHash as string),
        description: truncate(txHash as string),
        type: 'success',
      });
    } catch (error) {
      console.log('### error', error);
      toastV2({
        title: t('ERROR'),
        description: (error as Error).message,
        type: 'error',
      });
    }
  };

  const handleBridge = async () => {
    try {
      if (!sendFromRequest) {
        throw new Error(
          'Bridge transaction is still being prepared. Please wait and try again.'
        );
      }

      const txHash = await sendFrom(
        sendFromRequest as Parameters<typeof sendFrom>[0]
      );

      const newTransaction: TTransaction = {
        status: MessageStatus.INFLIGHT,
        userAddress: userAddress!,
        srcChainId: chainId as NetworkChainIds,
        dstChainId: bridgeState.dstChainId as NetworkChainIds,
        toAddress: toAddressBytes ?? '0x',
        amount: parseUnits(
          bridgeState.amount.toString(),
          bridgeState.token?.decimals ?? 18
        ).toString(),
        minAmount: minTokenAmount.toString(),
        srcTxHash: txHash,
        dstTxHash: '0x',
        nativeFee: nativeFee.toString(),
        token: bridgeState.token!,
        initialNonce: '0',
      };
      const previousTransactions = transactions;
      setTransactions([...previousTransactions, newTransaction]);

      // reset amounts on success
      const prevState = bridgeState;
      setBridgeState({
        ...prevState,
        amount: 0,
        tokenFee: BigInt(0),
      });

      // inform user
      toastV2({
        title:
          t('BRIDGE_SUCCESSFUL') +
          ' ' +
          getNetworkFromChainId(bridgeState.dstChainId).name,
        type: 'success',
        hash: truncate(txHash as string),
        description: 'Please wait for the transaction to be received.',
      });
    } catch (error) {
      console.log('### error', error);
      toastV2({
        title: t('ERROR'),
        description: (error as Error).message,
        type: 'error',
      });
    }
  };

  // if dst oft is a proxy...
  const maxBridgeAmountForProxy =
    outboundAmountOnProxy / BigInt(outboundAmountMultiplier); // max amount that can be bridged with proxy

  const hasEnoughProxyLiquidity =
    maxBridgeAmountForProxy >=
    parseUnits(
      bridgeState.amount.toString() ?? '0',
      bridgeState.token?.decimals ?? DEFAULT_TOKEN_DECIMALS
    ); // check if there is liquidity in proxy

  const insufficientBalance =
    sourceTokenBalance === BigInt(0) ||
    (sourceTokenBalance < bnTokenAmount && bridgeState.amount > 0);

  const userNativeValue = userNativeBalance?.value ?? BigInt(0);

  const unsupportedNetwork = !allowedNetworks.includes(chain?.id ?? 0);
  const approvalUnavailable =
    approvalSimulate.isLoading ||
    approvalSimulate.isError ||
    !approvalRequest ||
    approveStatus === 'pending';
  const bridgeUnavailable =
    bridgeState.amount === 0 ||
    insufficientBalance ||
    minTokenAmount === BigInt(0) || // amount too low
    nativeFee === BigInt(0) ||
    userNativeValue < nativeFee || // user has insufficient gas, when not connected value does not exist
    (!hasEnoughProxyLiquidity && destinationOftIsProxy) || // amount too big when dst oft is proxy
    bridgeState.tokenQuoteLoading || // app is loading quote
    isEstimatedSendFeeLoading ||
    isEstimatedSendFeeError ||
    sendFromSimulate.isError ||
    sendStatus === 'pending' ||
    sendFromSimulate.isLoading ||
    !sendFromRequest;
  const disabled = unsupportedNetwork
    ? false
    : allowanceRequired
      ? approvalUnavailable
      : bridgeUnavailable;

  const loading = allowanceRequired
    ? approvalSimulate.isLoading || approveStatus === 'pending'
    : sendFromSimulate.isLoading ||
      bridgeState.tokenQuoteLoading ||
      isEstimatedSendFeeLoading ||
      sendStatus === 'pending';

  const buttonAction = unsupportedNetwork
    ? handleSwitchNetwork
    : allowanceRequired
      ? handleApproveOft
      : handleBridge;

  const bridgeMessage: string = unsupportedNetwork // bridge disabled for destination chain
    ? t('migrate:SWITCH_NETWORK')
    : allowanceRequired // user has insufficient allowance
      ? t('APPROVE')
      : insufficientBalance
        ? t('INSUFFICIENT') + ' ' + t('BALANCE')
        : userNativeValue < nativeFee // user has insufficient gas balance, when not connected value does not exist
          ? t('INSUFFICIENT_GAS')
          : destinationOftIsProxy && !hasEnoughProxyLiquidity // destination oft is proxy and has insufficient liquidity
            ? t('BRIDGE_LIQUIDITY_ERROR')
            : 'Bridge'; // user can bridge

  return (
    <div className='flex w-full flex-col gap-5 rounded-[20px] border border-[#333333] bg-black p-8 md:w-1/2'>
      <BridgeInputField
        chainId={chainId}
        allowedNetworks={allowedNetworks}
        tokenBalance={sourceTokenBalance}
        destinationTokenBalance={destinationTokenBalance}
        destinationProxyLiquidity={
          destinationOftIsProxy &&
          !isOutboundAmountLoading &&
          !isOutboundAmountError
            ? maxBridgeAmountForProxy
            : undefined
        }
        isDestinationProxyLiquidityLoading={
          destinationOftIsProxy && isOutboundAmountLoading
        }
      />
      <BridgeUserBalanceAndFee
        tokenBalance={sourceTokenBalance}
        tokenFee={tokenFee}
        nativeFee={nativeFee}
        maxBridgeAmountProxy={maxBridgeAmountForProxy} // set above
      />
      {rendered && !userAddress ? (
        <ConnectButton className='px-10 py-2 text-base' />
      ) : (
        rendered && (
          <BridgeActionButton
            title={bridgeMessage}
            disabled={disabled}
            loading={loading}
            action={buttonAction}
          />
        )
      )}
      {transactions.length > 0 && (
        <BridgeTransactions
          transactions={transactions}
          user={userAddress ?? '0x'}
        />
      )}
    </div>
  );
};

interface BridgeACtionButtonProps {
  title: string;
  disabled: boolean;
  loading?: boolean;
  action: () => void;
}

function BridgeActionButton({
  title,
  disabled,
  loading = false,
  action,
}: BridgeACtionButtonProps) {
  return (
    <ButtonV2
      variant='default'
      loading={loading}
      variantStyle='quaternary'
      disabled={disabled}
      size='medium'
      className='font-haben !rounded-full !bg-[#FFFFFF] px-6 !text-box-primary'
      onClick={action}
    >
      {title}
    </ButtonV2>
  );
}
